import React, { type FC } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { CardShell } from './card-shell';
import type { ToolCardProps } from './types';
import { inferToolEntityType } from '@/hooks/agent-stream';
import { formatToolCardValue } from './format-value';

const EXCLUDED_FIELDS = new Set([
  'id',
  'order',
  'userId',
  'companyId',
  'threadId',
]);

const MAX_PAIRS = 4;

function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function humanizeFieldName(field: string): string {
  // Convert camelCase to separate words, then capitalize first letter
  return capitalize(
    field
      .replace(/([A-Z])/g, ' $1')
      .trim()
      .toLowerCase(),
  );
}

function deriveEntityType(props: Pick<ToolCardProps, 'entityType' | 'category' | 'toolName'>): string {
  if (props.entityType) return props.entityType;
  if (props.category) return props.category;
  return inferToolEntityType(props.toolName) || 'profile';
}


export const ProfileUpdateCard: FC<ToolCardProps> = ({
  args,
  toolName,
  category,
  entityType: entityTypeProp,
}) => {
  const entityType = deriveEntityType({
    entityType: entityTypeProp,
    category,
    toolName,
  });

  const entries = Object.entries(args)
    .filter(([key]) => !EXCLUDED_FIELDS.has(key))
    .filter(([, value]) => value != null && value !== '')
    .slice(0, MAX_PAIRS);

  return (
    <CardShell>
      <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <CheckCircle2 size={16} color="var(--text-secondary)" style={{ flexShrink: 0 }} />
          <span
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: 'var(--text-primary)',
            }}
          >
            Updated {entityType}
          </span>
        </div>

        {/* Key-value pairs */}
        {entries.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {entries.map(([key, value]) => (
              <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <span
                  style={{
                    fontSize: 12,
                    color: 'var(--text-tertiary)',
                  }}
                >
                  {humanizeFieldName(key)}
                </span>
                <span
                  style={{
                    fontSize: 14,
                    color: 'var(--text-primary)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formatToolCardValue(value, key)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </CardShell>
  );
};
